import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import Footer from "@/components/Footer";
import RepExample from "@/components/RepExample";
import PageMeta from "@/components/PageMeta";
import { SITE, has, ratesValid, lateFeeText } from "@/config/site";
import { amortize, paystackFee, fmtNGN, fmtNGNWhole, lateFeePerMiss, aprIncludingFee } from "@/lib/finance";
import { Calculator, ArrowRight, CheckCircle } from "@phosphor-icons/react";

export default function Pricing() {
  const lending = SITE.LENDING_ENABLED;
  const figures = lending && ratesValid();

  return (
    <div className="min-h-screen bg-[#F9F7F4]">
      <PageMeta
        title="Pricing & costs — Ascentremit"
        description="See exactly what school-fee instalments and payments cost on Ascentremit. The price is on the page: your monthly payment, total repayment and late fee — all before you sign."
      />
      <PublicNav />

      <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-16 py-14 lg:py-20">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#132A13] font-medium leading-tight">
          Pay school fees monthly.
          <br />
          <span className="italic text-[#0A3622]">Know the full cost before you start.</span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-[#4F5D4F] max-w-2xl leading-relaxed">
          Spread your child's fees over {SITE.TENORS_MONTHS[0]} to {SITE.TENORS_MONTHS[SITE.TENORS_MONTHS.length - 1]} months.
          The school is paid in full straight away, and you see exactly what you'll repay, down to the last naira,
          before you accept.
        </p>

        {lending && <InstalmentOverview />}
        {figures && <ExampleTable />}
        {figures && <LoanCalculator />}

        <OtherWays />
        <ForSchools />
        <ForAgents />

        <p className="text-xs text-[#4F5D4F] mt-16 leading-relaxed max-w-3xl" data-testid="pricing-small-print">
          {lending && (
            <>
              Instalment plans are loans, subject to an affordability assessment and approval. Borrow only what you
              can repay.{" "}
            </>
          )}
          Processing charges are set by Paystack and Stripe and may change; we always show the current amount before
          you pay.
        </p>
      </section>

      <RepExample />
      <Footer />
    </div>
  );
}

/* ───── Section: School-fee instalments overview ───── */
function InstalmentOverview() {
  const rows = [
    {
      k: "How long",
      v: `${SITE.TENORS_MONTHS.join(", ")} months — you choose`,
    },
    ratesValid() && {
      k: "Interest",
      v: `From ${SITE.RATE_MIN_PA}% to ${SITE.RATE_MAX_PA}% a year, set by your assessment and shown before you accept`,
    },
    has(SITE.APPLICATION_FEE_PCT) && {
      k: "Processing fee",
      v: `A one-time ${SITE.APPLICATION_FEE_PCT}% of the amount you borrow, added to your loan and included in the total you repay. The school always receives the full fee.`,
    },
    !SITE.EARLY_REPAYMENT_PENALTY && {
      k: "Paying off early",
      v: "No penalty — you pay interest only for the months you borrowed",
    },
    has(SITE.LATE_FEE_PCT) && {
      k: "Missed payment",
      v: `A late fee of ${lateFeeText()}`,
    },
    {
      k: "How you repay",
      v: `${SITE.REPAYMENT_METHOD_TEXT}. We send an SMS reminder ${SITE.REMINDER_DAYS_BEFORE} days before.`,
    },
    { k: "What the school gets", v: "The full fee, upfront" },
  ].filter(Boolean);

  return (
    <section className="mt-14" data-testid="pricing-instalment-overview">
      <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">School-fee instalments</h2>
      <div className="mt-5 rounded-2xl border border-[#E5E3DB] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.k} className={i > 0 ? "border-t border-[#E5E3DB]" : ""}>
                <td className="py-3.5 px-4 sm:px-5 font-semibold text-[#132A13] align-top w-40 sm:w-56">{r.k}</td>
                <td className="py-3.5 px-4 sm:px-5 text-[#4F5D4F]">{r.v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ───── Section: What ₦100,000 costs per plan ───── */
function ExampleTable() {
  const principal = 100000;
  const rate = SITE.RATE_REPRESENTATIVE_PA;
  const feePct = has(SITE.APPLICATION_FEE_PCT) ? SITE.APPLICATION_FEE_PCT : 0;
  const procFee = principal * feePct / 100;
  const rows = SITE.TENORS_MONTHS.map((n) => {
    const a = amortize(principal, rate, n);
    const totalWithFee = a.total + procFee;
    const costWithFee = a.interest + procFee;
    return { n, monthly: a.monthly, total: totalWithFee, cost: costWithFee };
  });

  return (
    <section className="mt-14" data-testid="pricing-example-table">
      <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">
        What a {fmtNGNWhole(principal)} fee costs on each plan
      </h2>
      <p className="text-sm text-[#4F5D4F] mt-2 max-w-2xl">
        At the representative rate of <span className="font-semibold text-[#132A13]">{rate}%</span> a year,
        {feePct > 0 && (
          <> including the one-time <span className="font-semibold text-[#132A13]">{feePct}%</span> processing fee of {fmtNGNWhole(procFee)}.</>
        )}{" "}
        Your rate is confirmed before you accept.
      </p>
      <div className="mt-5 rounded-2xl border border-[#E5E3DB] bg-white overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead className="bg-[#F0EFEA]">
            <tr className="text-left text-xs uppercase tracking-[0.12em] text-[#4F5D4F]">
              <th className="py-3 px-4 sm:px-5 font-semibold">Plan</th>
              <th className="py-3 px-4 sm:px-5 font-semibold">Monthly payment</th>
              <th className="py-3 px-4 sm:px-5 font-semibold">Processing fee</th>
              <th className="py-3 px-4 sm:px-5 font-semibold">Total you repay</th>
              <th className="py-3 px-4 sm:px-5 font-semibold">Cost of the loan</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.n} className="border-t border-[#E5E3DB]">
                <td className="py-3.5 px-4 sm:px-5 font-semibold text-[#132A13]">{r.n} months</td>
                <td className="py-3.5 px-4 sm:px-5 text-[#132A13] font-mono">{fmtNGNWhole(r.monthly)}</td>
                <td className="py-3.5 px-4 sm:px-5 text-[#4F5D4F] font-mono">{fmtNGNWhole(procFee)}</td>
                <td className="py-3.5 px-4 sm:px-5 text-[#132A13] font-mono">{fmtNGNWhole(r.total)}</td>
                <td className="py-3.5 px-4 sm:px-5 text-[#4F5D4F] font-mono">{fmtNGNWhole(r.cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-sm text-[#4F5D4F] mt-4 max-w-2xl">
        Shorter plans cost less overall; longer plans mean smaller monthly payments. Choose the plan you can
        comfortably afford each month.
      </p>
      <Link to="/parent/academic-advance" className="btn-primary mt-6" data-testid="pricing-cta-borrow">
        Check what you can borrow
        <ArrowRight size={16} weight="bold" />
        {SITE.SOFT_CHECK_AVAILABLE && (
          <span className="text-xs font-normal opacity-80 ml-2">— soft check, no effect on your credit record</span>
        )}
      </Link>
    </section>
  );
}

/* ───── Live loan calculator ───── */
function LoanCalculator() {
  const [amount, setAmount] = useState(150000);
  const [tenor, setTenor] = useState(SITE.TENORS_MONTHS[1] || SITE.TENORS_MONTHS[0]);
  const rate = SITE.RATE_REPRESENTATIVE_PA;
  const feePct = has(SITE.APPLICATION_FEE_PCT) ? SITE.APPLICATION_FEE_PCT : 0;

  const result = useMemo(() => {
    const a = amortize(amount, rate, tenor);
    const procFee = amount * feePct / 100;
    return {
      monthly: a.monthly,
      total: a.total + procFee,
      cost: a.interest + procFee,
      procFee,
      aprInclFee: feePct > 0 ? aprIncludingFee(amount, rate, tenor, feePct) : rate,
      lateFee: lateFeePerMiss(a.monthly, SITE.LATE_FEE_PCT, SITE.LATE_FEE_CAP_NGN),
    };
  }, [amount, tenor, rate, feePct]);

  return (
    <section className="mt-16" data-testid="pricing-calculator">
      <div className="rounded-3xl overflow-hidden border border-[#E5E3DB] bg-white grid lg:grid-cols-12">
        {/* Inputs */}
        <div className="lg:col-span-5 bg-[#0A3622] text-white p-6 sm:p-8">
          <div className="flex items-center gap-2 overline !text-[#F2B632]">
            <Calculator size={14} weight="bold" /> Live calculator
          </div>
          <h3 className="font-display text-2xl sm:text-3xl mt-3 font-medium tracking-tight leading-tight">
            Try your own numbers.
          </h3>
          <div className="mt-6 space-y-5">
            <div>
              <div className="flex items-baseline justify-between mb-2">
                <label className="text-xs uppercase tracking-[0.14em] text-white/60 font-semibold">
                  School fee amount
                </label>
                <span className="font-mono text-[#F2B632] font-medium">{fmtNGNWhole(amount)}</span>
              </div>
              <input
                type="range" min="20000" max="2000000" step="5000" value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full accent-[#F2B632]"
                data-testid="calc-amount-slider"
              />
              <input
                type="number" min="1000" value={amount}
                onChange={(e) => setAmount(Number(e.target.value || 0))}
                className="input mt-2 !bg-white/10 !border-white/15 !text-white !placeholder-white/40"
                data-testid="calc-amount-input"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.14em] text-white/60 font-semibold">Months to repay</label>
              <div className={`grid gap-2 mt-2`} style={{ gridTemplateColumns: `repeat(${SITE.TENORS_MONTHS.length}, minmax(0, 1fr))` }}>
                {SITE.TENORS_MONTHS.map((n) => (
                  <button
                    key={n} type="button"
                    onClick={() => setTenor(n)}
                    className={`py-2 rounded-full text-sm font-medium transition ${tenor === n ? "bg-[#F2B632] text-[#0A3622]" : "bg-white/10 text-white/80 hover:bg-white/15"}`}
                    data-testid={`calc-tenor-${n}`}
                  >
                    {n} mo
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Outputs */}
        <div className="lg:col-span-7 p-6 sm:p-8">
          <div className="overline mb-2">Result</div>
          <h4 className="font-display text-xl sm:text-2xl font-medium text-[#132A13]">
            On a {fmtNGNWhole(amount)} fee over {tenor} months…
          </h4>

          <div className="grid grid-cols-2 gap-3 mt-6">
            <Cell label="Monthly payment" value={fmtNGNWhole(result.monthly)} tone="primary" testid="calc-monthly" />
            <Cell label="Total you repay" value={fmtNGNWhole(result.total)} tone="neutral" testid="calc-total" />
            <Cell label="Cost of the loan" value={fmtNGNWhole(result.cost)} tone="warning" testid="calc-interest" />
            <Cell
              label="Late fee per missed instalment"
              value={fmtNGNWhole(result.lateFee)}
              tone="danger"
              testid="calc-late"
            />
          </div>

          {has(SITE.LATE_FEE_PCT) && (
            <p className="text-xs text-[#4F5D4F] mt-4 leading-relaxed" data-testid="calc-late-fee-text">
              Late fee: {lateFeeText()}
            </p>
          )}

          {feePct > 0 && (
            <p className="text-xs text-[#4F5D4F] mt-3 leading-relaxed" data-testid="calc-apr-incl-fee">
              Includes a one-time processing fee of <span className="font-semibold text-[#132A13]">{feePct}%</span>{" "}
              ({fmtNGNWhole(result.procFee)}), added to your loan. APR including the fee is{" "}
              <span className="font-semibold text-[#132A13]">{result.aprInclFee}%</span>.
            </p>
          )}

          <p className="text-xs text-[#4F5D4F] mt-3 leading-relaxed">
            Representative rate. Your actual rate, from <span className="font-semibold text-[#132A13]">{SITE.RATE_MIN_PA}%</span>
            {" "}to <span className="font-semibold text-[#132A13]">{SITE.RATE_MAX_PA}%</span> a year, is confirmed before you accept.
          </p>

          <Link to="/parent/academic-advance" className="btn-primary mt-6" data-testid="calc-cta-register">
            Check what you can borrow <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Cell({ label, value, tone = "neutral", testid }) {
  const tones = {
    neutral: "bg-[#F9F7F4] text-[#132A13]",
    primary: "bg-[#0A3622] text-white",
    warning: "bg-[#FBF1DC] text-[#B45309]",
    danger: "bg-[#FBE3E3] text-[#C93B3B]",
  };
  return (
    <div className={`rounded-xl p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-[0.14em] opacity-80 font-semibold leading-tight">{label}</div>
      <div className="font-display text-xl font-semibold mt-1.5 leading-none">{value}</div>
    </div>
  );
}

/* ───── Section: Other ways to pay ───── */
function OtherWays() {
  const sample = 150000;
  const psFee = paystackFee(sample);
  return (
    <section className="mt-16" data-testid="pricing-other-ways">
      <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">Other ways to pay</h2>
      <div className="mt-5 rounded-2xl border border-[#E5E3DB] bg-white overflow-hidden">
        <table className="w-full text-sm">
          <tbody>
            <tr>
              <td className="py-3.5 px-4 sm:px-5 font-semibold text-[#132A13] align-top w-56">Pay the full fee now</td>
              <td className="py-3.5 px-4 sm:px-5 text-[#4F5D4F]">
                Nothing from Ascentremit. Paystack adds a small processing charge, shown before you pay
                (e.g. on a {fmtNGNWhole(sample)} fee: {fmtNGNWhole(psFee)}).
              </td>
            </tr>
            <tr className="border-t border-[#E5E3DB]">
              <td className="py-3.5 px-4 sm:px-5 font-semibold text-[#132A13] align-top">Pay from abroad in US dollars</td>
              <td className="py-3.5 px-4 sm:px-5 text-[#4F5D4F]">
                Stripe's charge and any currency conversion, shown before you pay.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ───── Section: For schools ───── */
function ForSchools() {
  const sampleFee = 150000;
  const platformCut = sampleFee * (SITE.PLATFORM_FEE_PCT / 100);
  const schoolReceives = sampleFee - platformCut;
  return (
    <section className="mt-16" data-testid="pricing-for-schools">
      <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">For schools</h2>
      <p className="mt-2 text-sm sm:text-base text-[#4F5D4F]">
        Free to join. Get every fee in full, even when parents pay monthly.
      </p>
      <ul className="mt-5 space-y-3 text-sm">
        <Bullet>
          We charge <span className="font-semibold text-[#132A13]">{SITE.PLATFORM_FEE_PCT}%</span> of each fee collected
          — on a {fmtNGNWhole(sampleFee)} fee you receive{" "}
          <span className="font-semibold text-[#132A13]">{fmtNGNWhole(schoolReceives)}</span>.
        </Bullet>
        {SITE.LENDING_ENABLED && (
          <Bullet>
            When a parent chooses instalments, you still receive the whole fee upfront; we carry the repayment
            risk, not you.
          </Bullet>
        )}
        <Bullet>No setup fee, no monthly fee, no contract.</Bullet>
      </ul>
    </section>
  );
}

/* ───── Section: For agents ───── */
function ForAgents() {
  if (!has(SITE.AGENT_COMMISSION_TEXT)) return null;
  return (
    <section className="mt-16" data-testid="pricing-for-agents">
      <h2 className="font-display text-2xl sm:text-3xl font-medium text-[#132A13]">For agents</h2>
      <p className="mt-2 text-sm sm:text-base text-[#4F5D4F]" data-testid="pricing-agent-commission">
        {SITE.AGENT_COMMISSION_TEXT}.
      </p>
    </section>
  );
}

function Bullet({ children }) {
  return (
    <li className="flex gap-3">
      <CheckCircle size={18} weight="fill" className="text-[#0A3622] mt-0.5 shrink-0" />
      <span className="text-[#132A13] leading-relaxed">{children}</span>
    </li>
  );
}
